// QuadraticGeneralInequalityTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import ParabolaShape from "../../../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";
import {
  discriminantSubstitutionLatex,
  substitutedCoefficientLatex,
} from "../../../../utils/quadraticFormulaLatex";
import { formatApproximateMixedFractionLatex as formatMixedFractionLatex } from "../../../../utils/fractionLatex";

/* ===== helpers ===== */
const SIGN_KINDS = ["lt", "gt", "le", "ge"]; // < > ≤ ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };

function gcd(left, right) {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

const gcd3 = (a, b, c) => gcd(gcd(a, b), c);

// ładny zapis ax^2+bx+c
function quadraticLatex(a, b, c) {
  const ax = a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`;
  const bx =
    b === 0 ? "" : b === 1 ? "+ x" : b === -1 ? "- x" : b > 0 ? `+ ${b}x` : `- ${Math.abs(b)}x`;
  const cx = c === 0 ? "" : c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`;
  return `${ax} ${bx} ${cx}`.replace(/\s+/g, " ").trim();
}

/* ===== generator ===== */
export function generateTask(options) {
  const forceKind = typeof options === "string" ? options : options?.kind;
  const randi = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  // a ≠ 0 i |a| ≠ 1, by 2a nie było trywialne
  let a = 0;
  while (a === 0 || Math.abs(a) === 1) a = randi(-5, 5);

  let r1, r2, b, c;
  do {
    r1 = randi(-6, 5);
    r2 = randi(r1 + 1, 6);
    b = -a * (r1 + r2);
    c = a * r1 * r2;
  } while (b === 0 || c === 0 || Math.abs(b) > 24 || Math.abs(c) > 60);

  const disc = b * b - 4 * a * c;
  const sqrtDisc = Math.trunc(Math.sqrt(disc));
  const commonDivisor = gcd3(a, b, c);
  const reducedA = a / commonDivisor;
  const reducedB = b / commonDivisor;
  const reducedC = c / commonDivisor;
  const reducedDisc = reducedB * reducedB - 4 * reducedA * reducedC;
  const reducedSqrtDisc = Math.trunc(Math.sqrt(reducedDisc));

  const kind = forceKind ?? SIGN_KINDS[randi(0, SIGN_KINDS.length - 1)];
  const exprLatex = `${quadraticLatex(a, b, c)}\\;${SIGN_LATEX[kind]}\\;0`;

  return {
    type: "QuadraticGeneralInequalityTask",
    data: {
      a, b, c, disc, sqrtDisc, r1, r2, kind, exprLatex,
      commonDivisor, reducedA, reducedB, reducedC, reducedDisc, reducedSqrtDisc,
    },
  };
}

/* ===== UI ===== */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż nierówność: <InlineMath math={data.exprLatex} />
    </p>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={"x\\;\\in"} />
        </div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 1) ∪ (4, ∞) albo (1, 4)"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw =
    typeof userInput === "string"
      ? userInput
      : userInput?.S?.raw || userInput?.S || "";
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

/* ===== logika rozwiązania ===== */
function solutionFor({ a, r1, r2, kind }) {
  const inclusive = kind === "le" || kind === "ge";
  // > / ≥ -> na zewnątrz przy a>0, < / ≤ -> na zewnątrz przy a<0
  const needOutside = (kind === "gt" || kind === "ge") ? a > 0 : a < 0;
  if (needOutside) {
    return {
      kind: "outside",
      inclusive,
      left:  { L: -Infinity, R: r1, Rclosed: inclusive },
      right: { L: r2,        R: Infinity, Lclosed: inclusive },
    };
  }
  return {
    kind: "between",
    inclusive,
    seg: { L: r1, R: r2, Lclosed: inclusive, Rclosed: inclusive },
  };
}

function intervalsEqual(ans, parsed) {
  if (!parsed || parsed.kind !== "intervals") return false;
  const eq = (x, y, tol = 1e-9) => Math.abs(x - y) <= tol;
  const norm = (I) => ({
    L: I.left, R: I.right, Lc: !!I.leftClosed, Rc: !!I.rightClosed,
  });

  const expected = [];
  if (ans.kind === "outside") {
    expected.push({ L: -Infinity, R: ans.left.R,  Lc: false, Rc: !!ans.left.Rclosed });
    expected.push({ L: ans.right.L, R: Infinity, Lc: !!ans.right.Lclosed, Rc: false });
  } else {
    expected.push({ L: ans.seg.L, R: ans.seg.R, Lc: !!ans.seg.Lclosed, Rc: !!ans.seg.Rclosed });
  }

  const got = parsed.intervals.map(norm);
  if (expected.length !== got.length) return false;

  const used = new Array(got.length).fill(false);
  for (const E of expected) {
    let ok = false;
    for (let i = 0; i < got.length; i++) {
      if (used[i]) continue;
      const G = got[i];
      const Lok = E.L === -Infinity ? G.L === -Infinity : eq(E.L, G.L);
      const Rok = E.R ===  Infinity ? G.R ===  Infinity : eq(E.R, G.R);
      if (Lok && Rok && E.Lc === G.Lc && E.Rc === G.Rc) { used[i] = true; ok = true; break; }
    }
    if (!ok) return false;
  }
  return true;
}

export function checkAnswer({ data }, userInput = {}) {
  const raw =
    typeof userInput?.S === "string"
      ? userInput.S
      : userInput?.S?.raw || "";
  if (!raw) return { correct: false };
  const parsed = parseIntervalInput(raw);
  const ans = solutionFor({ a: data.a, r1: data.r1, r2: data.r2, kind: data.kind });
  return { correct: intervalsEqual(ans, parsed) };
}

/* ===== poprawna odpowiedź ===== */
export function renderAnswer({ data }) {
  const ans = solutionFor(data);
  const { r1, r2 } = data;
  const R1 = formatMixedFractionLatex(r1);
  const R2 = formatMixedFractionLatex(r2);
  const rb = ans.inclusive ? "]" : ")";
  const lb = ans.inclusive ? "[" : "(";
  if (ans.kind === "outside") {
    return (
      <InlineMath math={`x\\in (-\\infty,\\; ${R1}${rb}\\;\\cup\\; ${lb}${R2},\\; \\infty)`} />
    );
  }
  return <InlineMath math={`x\\in ${lb}${R1},\\; ${R2}${rb}`} />;
}

/* ===== wyjaśnienie =====
   Krok 1: podstawienia do Δ i osobno rachunki dla x1 (minus) i x2 (plus).
   Krok 2: szkic + odczyt przedziału z wykresu (bez zapisu odpowiedzi i bez osi).
   Krok 3: zapis odpowiedzi i zaznaczenie na osi liczbowej.
*/
export function renderExplanation({ data }) {
  const {
    a, r1, r2, kind, exprLatex, commonDivisor,
    reducedA, reducedB, reducedC, reducedDisc, reducedSqrtDisc,
  } = data;
  const ans = solutionFor({ a, r1, r2, kind });

  const aPos = a > 0;
  const armsTxt = aPos ? "ramiona do góry" : "ramiona w dół";
  const chooseTxt =
    kind === "gt" || kind === "ge"
      ? "bierzemy część wykresu powyżej osi OX"
      : "bierzemy część wykresu poniżej osi OX";
  const inclusiveNote = ans.inclusive
    ? "Ponieważ znak jest „≤/≥”, miejsca zerowe także należą do rozwiązania."
    : "Miejsca zerowe nie należą do rozwiązania (otwarte końce).";

  // ładne ułamki
  const R1_ltx = formatMixedFractionLatex(r1);
  const R2_ltx = formatMixedFractionLatex(r2);

  // zapisy w Δ i -b
  const minusBLatex = reducedB < 0 ? `-(${reducedB})` : `-${reducedB}`;
  const num = substitutedCoefficientLatex;
  const deltaSubstitution = discriminantSubstitutionLatex(
    reducedA,
    reducedB,
    reducedC,
    reducedDisc
  );
  const denom = 2 * reducedA;
  const denomFormulaLatex = `2\\cdot ${num(reducedA)}`;
  const rootVariants = [
    {
      sign: "-",
      numerator: -reducedB - reducedSqrtDisc,
      value: (-reducedB - reducedSqrtDisc) / denom,
    },
    {
      sign: "+",
      numerator: -reducedB + reducedSqrtDisc,
      value: (-reducedB + reducedSqrtDisc) / denom,
    },
  ].sort((left, right) => left.value - right.value);

  // Krok 1 – mocno skondensowane, bez „pół-symbolicznej” linii
  const rootLine = (label, root, resultLatex) => String.raw`
\begin{aligned}
${label} &= \dfrac{${minusBLatex} ${root.sign} ${reducedSqrtDisc}}{${denomFormulaLatex}} \\[4pt]
${label} &= \dfrac{${root.numerator}}{${denom}} \\[4pt]
${label} &= ${resultLatex}
\end{aligned}
`;

  const x1Lines = rootLine("x_1", rootVariants[0], R1_ltx);
  const x2Lines = rootLine("x_2", rootVariants[1], R2_ltx);

  const solLatex =
    ans.kind === "outside"
      ? String.raw`x\in (-\infty,\; ${R1_ltx}${ans.inclusive ? "]" : ")"}\ \cup\ ${ans.inclusive ? "[" : "("}${R2_ltx},\; \infty)`
      : String.raw`x\in ${ans.inclusive ? "[" : "("}${R1_ltx},\; ${R2_ltx}${ans.inclusive ? "]" : ")"}`;

  const verbalReadNode =
    ans.kind === "outside" ? (
      <p style={{ textAlign: "center" }}>
        Odczyt: rozwiązanie leży na zewnątrz przedziału{" "}
        <InlineMath math={`(${R1_ltx},\\; ${R2_ltx})`} />
        {ans.inclusive ? " wraz z końcami." : "."}
      </p>
    ) : (
      <p style={{ textAlign: "center" }}>
        Odczyt: rozwiązanie leży pomiędzy <InlineMath math={R1_ltx} /> i{" "}
        <InlineMath math={R2_ltx} />
        {ans.inclusive ? ", włącznie z końcami." : "."}
      </p>
    );

  return (
    <div className="space-y-3">
      <p>
        <strong>Krok 1:</strong> Upraszczamy nierówność, dzieląc wszystkie
        współczynniki przez <InlineMath math={`${commonDivisor}`} />. Dzielimy
        przez liczbę dodatnią, więc znak nierówności się nie zmienia.
      </p>
      <BlockMath math={`${exprLatex}\\quad |:${commonDivisor}`} />
      <BlockMath
        math={`${quadraticLatex(reducedA, reducedB, reducedC)}\\;${SIGN_LATEX[kind]}\\;0`}
      />

      <p><strong>Krok 2:</strong> Liczymy miejsca zerowe z delty.</p>
      <BlockMath math={String.raw`\Delta = ${deltaSubstitution}`} />
      <BlockMath math={String.raw`\sqrt{\Delta} = ${reducedSqrtDisc}`} />

      {/* Dwie kolumny: x1 i x2 obok siebie (z responsywnym auto-fit) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={'x_1'} />
          </div>
          <BlockMath math={x1Lines} />
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={'x_2'} />
          </div>
          <BlockMath math={x2Lines} />
        </div>
      </div>

      <p>
        <strong>Krok 3:</strong> Rysujemy uproszczony wykres (
        <InlineMath math={`a=${a}`} /> — {aPos ? "dodatni" : "ujemny"}{" "}
        <InlineMath math={aPos ? "a>0" : "a<0"} /> → {armsTxt}
        ), szukamy gdzie <InlineMath math={exprLatex} /> — czyli {chooseTxt}.{" "}
        {inclusiveNote}
      </p>

      <div style={{ maxWidth: 620, margin: "0.5rem auto" }}>
        <ParabolaShape
          aSign={aPos ? 1 : -1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks
          curveColor="#f59e0b"
          labelLeftTex={R1_ltx}
          labelRightTex={R2_ltx}
          highlightMode={ans.kind === "outside" ? "outside" : "between"}
          highlightLeftClosed={ans.inclusive}
          highlightRightClosed={ans.inclusive}
          highlightColor="#a78bfa"
        />
      </div>

      {verbalReadNode}

      <p><strong>Krok 4:</strong> Zapisujemy odpowiedź i zaznaczamy ją na osi liczbowej.</p>

      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <InlineMath math={solLatex} />
      </div>

      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        {ans.kind === "outside" ? (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { to: r1, rightClosed: ans.inclusive, rightLabelLatex: R1_ltx },
              { from: r2, leftClosed: ans.inclusive, leftLabelLatex: R2_ltx },
            ]}
          />
        ) : (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              {
                from: r1,
                to: r2,
                leftClosed: ans.inclusive,
                rightClosed: ans.inclusive,
                leftLabelLatex: R1_ltx,
                rightLabelLatex: R2_ltx,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}

